import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  VideoStatusService as VideoStatusServiceApi,
  VideoQualityResponse,
  ComprehensiveVideoStatus,
  PaginatedVideoStatusResponse
} from '@solverml/api';
import { Observable, tap, finalize, catchError, of } from 'rxjs';

export type VideoStatusProcessFilter = 'all' | 'processing' | 'queued' | 'completed' | 'error';
export type VideoStatusSortOrder = 'queue_priority_desc' | 'queue_priority_asc' | 'latest_desc' | 'latest_asc';

export interface VideoStatusOverview {
  processing: number;
  queued: number;
  completed: number;
  error: number;
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class VideoStatusService {
  private api = inject(VideoStatusServiceApi);
  private http = inject(HttpClient);
  private _requestInFlight = false;
  private readonly backendBaseUrl = `${window.location.protocol}//backend.${window.location.hostname}`;

  private _activeProcesses = signal<ComprehensiveVideoStatus[]>([]);

  private _currentPage = signal<number>(1);
  private _pageSize = signal<number>(9);
  private _totalItems = signal<number>(0);
  private _processFilter = signal<VideoStatusProcessFilter>('all');
  private _sortOrder = signal<VideoStatusSortOrder>('latest_desc');
  private _searchQuery = signal<string>('');
  private _statusOverview = signal<VideoStatusOverview>({
    processing: 0,
    queued: 0,
    completed: 0,
    error: 0,
    total: 0
  });

  private _isLoading = signal<boolean>(false);
  private _error = signal<string | null>(null);

  public readonly activeProcesses = this._activeProcesses.asReadonly();
  public readonly currentPage = this._currentPage.asReadonly();
  public readonly pageSize = this._pageSize.asReadonly();
  public readonly totalItems = this._totalItems.asReadonly();
  public readonly processFilter = this._processFilter.asReadonly();
  public readonly sortOrder = this._sortOrder.asReadonly();
  public readonly searchQuery = this._searchQuery.asReadonly();
  public readonly statusOverview = this._statusOverview.asReadonly();
  public readonly isLoading = this._isLoading.asReadonly();
  public readonly error = this._error.asReadonly();

  public readonly hasActiveProcesses = computed(() => this._activeProcesses().length > 0);
  public readonly totalPages = computed(() =>
    Math.ceil(this._totalItems() / this._pageSize())
  );

  public loadComprehensiveStatuses(): void {
    if (this._requestInFlight) {
      return;
    }

    if (this._activeProcesses().length === 0) {
      this._isLoading.set(true);
    }

    this._error.set(null);
    this._requestInFlight = true;

    const params = new HttpParams()
      .set('page', String(this._currentPage()))
      .set('page_size', String(this._pageSize()))
      .set('process_filter', this._processFilter())
      .set('sort_order', this.toApiSortOrder(this._sortOrder(), this._processFilter()))
      .set('search_query', this._searchQuery().trim());

    this.http.get<PaginatedVideoStatusResponse>(
      `${this.backendBaseUrl}/video/status/comprehensive`,
      { params }
    ).pipe(
      tap((response: PaginatedVideoStatusResponse) => {
        const currentItems = this._activeProcesses();
        const incomingItems = this.sortStatusesForDisplay(response.items as ComprehensiveVideoStatus[]);
        const incomingOverview = (response as unknown as { status_overview?: Partial<VideoStatusOverview> }).status_overview;
        const previousOverview = this._statusOverview();
        const resolvedOverview: VideoStatusOverview = {
          processing: Number(incomingOverview?.processing ?? previousOverview.processing),
          queued: Number(incomingOverview?.queued ?? previousOverview.queued),
          completed: Number(incomingOverview?.completed ?? previousOverview.completed),
          error: Number(incomingOverview?.error ?? previousOverview.error),
          total: Number(incomingOverview?.total ?? previousOverview.total)
        };

        if (this.hasMeaningfulChanges(currentItems, incomingItems)) {
          this._activeProcesses.set(incomingItems);
        }
        this._statusOverview.set(resolvedOverview);

        if (response.total !== this._totalItems()) {
          this._totalItems.set(response.total);
        }

        if (response.page !== this._currentPage()) {
          this._currentPage.set(response.page);
        }
        if (response.page_size !== this._pageSize()) {
          this._pageSize.set(response.page_size);
        }
      }),
      catchError((err) => {
        console.error('Error fetching video statuses', err);
        this._error.set('No se pudo actualizar el estado de los videos.');
        return of(null);
      }),
      finalize(() => {
        this._isLoading.set(false);
        this._requestInFlight = false;
      })
    ).subscribe();
  }

  public setPage(page: number): void {
    if (page !== this._currentPage()) {
      this._currentPage.set(page);
      this._activeProcesses.set([]);
      this.loadComprehensiveStatuses();
    }
  }

  public setPageSize(size: number): void {
    if (size !== this._pageSize()) {
      this._pageSize.set(size);
      this._currentPage.set(1);
      this._activeProcesses.set([]);
      this.loadComprehensiveStatuses();
    }
  }

  public setProcessFilter(filter: VideoStatusProcessFilter): void {
    if (filter !== this._processFilter()) {
      this._processFilter.set(filter);
      const normalizedSort = this.normalizeSortForFilter(this._sortOrder(), filter);
      if (normalizedSort !== this._sortOrder()) {
        this._sortOrder.set(normalizedSort);
      }
      this._currentPage.set(1);
      this._activeProcesses.set([]);
      this.loadComprehensiveStatuses();
    }
  }

  public setSortOrder(order: VideoStatusSortOrder): void {
    const normalizedOrder = this.normalizeSortForFilter(order, this._processFilter());
    if (normalizedOrder !== this._sortOrder()) {
      this._sortOrder.set(normalizedOrder);
      this._currentPage.set(1);
      this._activeProcesses.set([]);
      this.loadComprehensiveStatuses();
    }
  }

  public setSearchQuery(query: string): void {
    const normalized = (query || '').trim();
    if (normalized !== this._searchQuery()) {
      this._searchQuery.set(normalized);
      this._currentPage.set(1);
      this._activeProcesses.set([]);
      this.loadComprehensiveStatuses();
    }
  }

  public clearState(): void {
    this._activeProcesses.set([]);
    this._error.set(null);
    this._isLoading.set(false);
    this._currentPage.set(1);
    this._processFilter.set('all');
    this._sortOrder.set('latest_desc');
    this._searchQuery.set('');
    this._statusOverview.set({
      processing: 0,
      queued: 0,
      completed: 0,
      error: 0,
      total: 0
    });
  }

  public getVideoQuality(videoId: string): Observable<VideoQualityResponse | null> {
    return this.api.getVideoQualityScore(videoId);
  }

  private hasMeaningfulChanges(
    current: ComprehensiveVideoStatus[],
    incoming: ComprehensiveVideoStatus[]
  ): boolean {
    if (current.length !== incoming.length) return true;

    const currentById = new Map<string, ComprehensiveVideoStatus>(
      current.map(item => [item.video_id, item])
    );

    for (const next of incoming) {
      const prev = currentById.get(next.video_id);
      if (!prev) return true;

      const prevLastTimeline = prev.history_log.at(-1)?.timestamp ?? null;
      const nextLastTimeline = next.history_log.at(-1)?.timestamp ?? null;

      const prevAny = prev as any;
      const nextAny = next as any;

      if (
        prev.current_stage !== next.current_stage ||
        prev.current_label !== next.current_label ||
        prev.current_status_type !== next.current_status_type ||
        prev.ia_progress_percentage !== next.ia_progress_percentage ||
        prev.queue_position !== next.queue_position ||
        prevAny.queue_type !== nextAny.queue_type ||
        prevAny.global_queue_position !== nextAny.global_queue_position ||
        prevAny.eta_min_seconds !== nextAny.eta_min_seconds ||
        prevAny.eta_max_seconds !== nextAny.eta_max_seconds ||
        prevAny.is_reprocess !== nextAny.is_reprocess ||
        prev.is_error !== next.is_error ||
        prev.error_message !== next.error_message ||
        prev.elapsed_minutes !== next.elapsed_minutes ||
        prev.history_log.length !== next.history_log.length ||
        prevLastTimeline !== nextLastTimeline
      ) {
        return true;
      }
    }

    return false;
  }

  private sortStatusesForDisplay(items: ComprehensiveVideoStatus[]): ComprehensiveVideoStatus[] {
    const sortOrder = this._sortOrder();
    const filter = this._processFilter();

    if (filter === 'queued') {
      if (sortOrder === 'queue_priority_asc') {
        return [...items].sort((a, b) => this.compareQueuePriority(a, b, true));
      }
      return [...items].sort((a, b) => this.compareQueuePriority(a, b, false));
    }

    if (sortOrder === 'latest_asc') {
      return [...items].sort((a, b) => this.getLastTimestamp(a) - this.getLastTimestamp(b));
    }

    return [...items].sort((a, b) => this.getLastTimestamp(b) - this.getLastTimestamp(a));
  }

  private compareQueuePriority(a: ComprehensiveVideoStatus, b: ComprehensiveVideoStatus, reversePriority: boolean): number {
    const keyA = this.getQueuePriorityKey(a, reversePriority);
    const keyB = this.getQueuePriorityKey(b, reversePriority);

    if (keyA.rank !== keyB.rank) return keyA.rank - keyB.rank;
    if (keyA.queueTypeRank !== keyB.queueTypeRank) return keyA.queueTypeRank - keyB.queueTypeRank;
    if (keyA.queuePosition !== keyB.queuePosition) return keyA.queuePosition - keyB.queuePosition;

    return keyB.lastTs - keyA.lastTs;
  }

  private getQueuePriorityKey(
    item: ComprehensiveVideoStatus,
    reversePriority: boolean
  ): { rank: number; queueTypeRank: number; queuePosition: number; lastTs: number } {
    const anyItem = item as any;
    const queueTypeRank = anyItem.queue_type === "priority"
      ? (reversePriority ? 2 : 1)
      : (reversePriority ? 1 : 2);
    const queuePos = Number(item.queue_position ?? 999999);
    return {
      rank: 1,
      queueTypeRank,
      queuePosition: queuePos,
      lastTs: this.getLastTimestamp(item)
    };
  }

  private normalizeSortForFilter(order: VideoStatusSortOrder, filter: VideoStatusProcessFilter): VideoStatusSortOrder {
    if (filter === 'queued') {
      if (order === 'queue_priority_desc' || order === 'queue_priority_asc') {
        return order;
      }
      return 'queue_priority_desc';
    }

    if (order === 'latest_desc' || order === 'latest_asc') {
      return order;
    }
    return 'latest_desc';
  }

  private toApiSortOrder(order: VideoStatusSortOrder, filter: VideoStatusProcessFilter): string {
    if (filter === 'queued') {
      return order === 'queue_priority_asc' ? 'queue_priority_asc' : 'queue_priority_desc';
    }
    return order === 'latest_asc' ? 'asc' : 'desc';
  }

  private getLastTimestamp(item: ComprehensiveVideoStatus): number {
    const raw = item.history_log.at(-1)?.timestamp;
    if (!raw) return 0;
    const ts = new Date(raw as string).getTime();
    return Number.isFinite(ts) ? ts : 0;
  }
}
